import React from 'react';
import PropTypes from 'prop-types';

/**
 * CanadianFlag — pixel art Canadian flag with proper maple leaf grid.
 *
 * The flag is rendered as three bands: red | white (with leaf) | red.
 * The maple leaf is built from a coordinate array of filled pixel cells,
 * each cell sized at 1.5 × 1.5 SVG units.
 *
 * Apply the `.flag-anim` class to the wrapping <g> to animate waving.
 * This component must be rendered inside an <svg> element.
 *
 * @param {object} props
 * @param {number} props.x - Left edge of the flag in SVG coordinates.
 * @param {number} props.y - Top edge of the flag in SVG coordinates.
 * @param {number} [props.w=28] - Total flag width (red | white | red).
 * @param {number} [props.h=18] - Total flag height.
 */
function CanadianFlag({ x, y, w, h }) {
  // The maple leaf is defined as [column, row] pixel cells within the
  // white centre panel.  Column 0 = leftmost, row 0 = topmost.
  const LEAF_PIXELS = [
    [1,0],[2,0],[3,0],[4,0],[5,0],[6,0],
    [0,1],[1,1],[2,1],[3,1],[4,1],[5,1],[6,1],[7,1],
    [1,2],[2,2],[3,2],[4,2],[5,2],[6,2],
    [2,3],[3,3],[4,3],[5,3],
    [1,4],[2,4],[3,4],[4,4],[5,4],[6,4],
    [0,5],[1,5],[2,5],[3,5],[4,5],[5,5],[6,5],[7,5],
    [1,6],[2,6],[3,6],[4,6],[5,6],[6,6],
    [3,7],[4,7],  // stem top
    [3,8],[4,8],  // stem base
  ];

  const CELL      = 1.5;   // pixel cell size in SVG units
  const BAND_W    = w / 4; // each red band is 1/4 of total width
  const WHITE_W   = w / 2;
  const whiteX    = x + BAND_W;

  return (
    <g>
      {/* Left red band */}
      <rect x={x}        y={y} width={BAND_W}  height={h} fill="#cc2222" />
      {/* White centre */}
      <rect x={whiteX}   y={y} width={WHITE_W} height={h} fill="#ffffff" />
      {/* Right red band */}
      <rect x={whiteX + WHITE_W} y={y} width={BAND_W} height={h} fill="#cc2222" />

      {/* Maple leaf — positioned within the white centre panel */}
      {LEAF_PIXELS.map(([col, row], idx) => (
        <rect
          key={idx}
          x={whiteX + col * CELL + 0.6}
          y={y      + row * CELL + 0.6}
          width={CELL}
          height={CELL}
          fill="#cc2222"
        />
      ))}
    </g>
  );
}

CanadianFlag.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  w: PropTypes.number,
  h: PropTypes.number,
};

CanadianFlag.defaultProps = {
  w: 28,
  h: 18,
};

export default CanadianFlag;
